import { RoleplaySessionTranscriptFragment } from "@/graphql";
import { Evaluation, FeedbackExample } from "@/types/evaluation";
import { ensureNonNullArray } from "@/utils/array";

/**
 * Grade ordering maps - determines the display priority of feedback examples
 * based on their grade and the current evaluation's grade
 */
// When evaluation grade is "Good", prefer showing Good examples first
const GRADE_ORDER_FOR_GOOD_EVAL: Record<string, number> = {
  Good: 3,
  Mid: 2,
  Bad: 1,
};

// When evaluation grade is NOT "Good", prefer showing examples matching the grade
const GRADE_ORDER_FOR_NON_GOOD_EVAL: Record<string, number> = {
  Good: 1,
  Mid: 2,
  Bad: 3,
};

// Map to determine which ordering to use based on the evaluation grade
const EVALUATION_GRADE_TO_EXAMPLE_ORDER_BY_GRADE: Record<
  string,
  Record<string, number>
> = {
  Good: GRADE_ORDER_FOR_GOOD_EVAL,
  Mid: GRADE_ORDER_FOR_NON_GOOD_EVAL,
  Bad: GRADE_ORDER_FOR_NON_GOOD_EVAL,
};

// Priority ordering for salience levels
const SALIENCE_ORDER: Record<string, number> = {
  HIGH: 3,
  MID: 2,
  LOW: 1,
};

// Interfaces for better type safety
interface ProcessedCriterionGroup {
  id: string;
  name: string;
  order: number;
  evaluations: Evaluation[];
}

interface EvaluationWithContext {
  evaluation: Evaluation;
  groupName: string;
}

/**
 * Formats a duration in seconds to a human-readable MM:SS format
 */
export function formatMinutes(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    const totalMinutes = hours * 60 + minutes;
    return `${totalMinutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Formats transcript messages for display
 */
export function formatMessagesData({
  session,
  isSessionOwner,
}: {
  session?: {
    transcripts?: Array<RoleplaySessionTranscriptFragment | null> | null;
    scenario: {
      personaName?: string;
      personaPhotoUrl?: string;
    };
    user?: {
      name?: string;
      photo?: string;
    };
  };
  isSessionOwner: boolean;
}): RoleplaySessionTranscriptFragment[] {
  if (!session) return [];

  const transcripts = ensureNonNullArray(session.transcripts);

  const bot = {
    name: session.scenario.personaName || "AI Assistant",
    photo: session.scenario.personaPhotoUrl || "",
  };

  const user = {
    name: isSessionOwner ? "You" : session.user?.name || "User",
    photo: session.user?.photo || "",
  };

  return transcripts.map(
    (msg: RoleplaySessionTranscriptFragment, index: number) => {
      const nextMsg = transcripts[index + 1];

      return {
        ...msg,
        id: msg.id,
        sender: msg.speakerId === "bot" ? bot : user,
        message: msg.text || "",
        time: formatMinutes(msg.secondsFromStart || 0),
        isUser: msg.speakerId !== "bot",
        secondsFromStart: msg.secondsFromStart || 0,
        nextMessageStart: nextMsg?.secondsFromStart,
        index,
      };
    },
  );
}

/**
 * Calculates Jaccard similarity between two texts
 * (the size of the intersection divided by the size of the union of word sets)
 *
 * @param text1 First text to compare
 * @param text2 Second text to compare
 * @returns A value between 0 (completely different) and 1 (identical)
 */
function calculateJaccardSimilarity(text1: string, text2: string): number {
  if (!text1 || !text2) return 0;

  // Convert texts to word sets
  const words1 = new Set(
    text1
      .toLowerCase()
      .split(/\s+/)
      .filter((w) => w.length > 0),
  );
  const words2 = new Set(
    text2
      .toLowerCase()
      .split(/\s+/)
      .filter((w) => w.length > 0),
  );

  // Empty texts should have 0 similarity
  if (words1.size === 0 || words2.size === 0) return 0;

  // For performance, use the smaller set for iteration
  const [smallerSet, largerSet] =
    words1.size <= words2.size ? [words1, words2] : [words2, words1];

  // Count intersection (optimized to iterate only through the smaller set)
  let intersectionCount = 0;
  for (const word of smallerSet) {
    if (largerSet.has(word)) {
      intersectionCount++;
    }
  }

  // Union size is the sum of both set sizes minus the intersection
  const unionCount = words1.size + words2.size - intersectionCount;

  // Return Jaccard similarity
  return intersectionCount / unionCount;
}

/**
 * Checks if two quotes are similar enough to be considered the same
 * Uses Jaccard similarity to handle variations in wording
 *
 * @param quote1 First quote to compare
 * @param quote2 Second quote to compare
 * @returns True if the quotes are considered similar
 */
function areSimilarQuotes(
  quote1: string | null | undefined,
  quote2: string | null | undefined,
): boolean {
  if (!quote1 || !quote2) return false;

  // Calculate Jaccard similarity
  const similarity = calculateJaccardSimilarity(quote1, quote2);

  // Quotes with 85% or higher similarity are considered the same
  return similarity >= 0.85;
}

/**
 * Sorts feedback examples within an evaluation based on grade and salience
 */
function sortFeedbackExamplesByGradeAndSalience<T extends FeedbackExample>(
  feedbackExamples: T[],
  evaluationGrade?: string | null,
): T[] {
  if (!feedbackExamples || feedbackExamples.length <= 1) {
    return feedbackExamples || [];
  }

  const gradeOrder =
    EVALUATION_GRADE_TO_EXAMPLE_ORDER_BY_GRADE[evaluationGrade ?? "Mid"] ||
    GRADE_ORDER_FOR_NON_GOOD_EVAL;

  return [...feedbackExamples].sort((a, b) => {
    if (!a || !b || !a.grade || !b.grade) return 0;

    // First sort by grade
    if (a.grade !== b.grade) {
      return (gradeOrder[b.grade] || 0) - (gradeOrder[a.grade] || 0);
    }

    // Then by salience if grades are equal
    const salienceA = SALIENCE_ORDER[a.salience!] || 0;
    const salienceB = SALIENCE_ORDER[b.salience!] || 0;
    return salienceB - salienceA;
  });
}

/**
 * Optimizes first quotes in each evaluation group for maximum diversity
 *
 * The goal is simple: make the first quote shown in each evaluation
 * as different as possible from previously seen first quotes.
 *
 * @param evaluations List of evaluations with their group context
 * @returns The same evaluations with potentially reordered feedback examples
 */
function optimizeFirstQuotes(
  evaluations: EvaluationWithContext[],
): EvaluationWithContext[] {
  if (!evaluations.length) return evaluations;

  // Keep track of quotes we've already shown in first position
  const seenFirstQuotes: string[] = [];

  // Helper function to check if a quote is similar to any previously seen quotes
  const isSimilarToAnySeenQuote = (
    quote: string | null | undefined,
  ): boolean => {
    if (!quote) return false;
    return seenFirstQuotes.some((seenQuote) =>
      areSimilarQuotes(quote, seenQuote),
    );
  };

  // First pass: Sort each evaluation's feedback examples by grade and salience
  evaluations.forEach(({ evaluation }) => {
    const feedbackExamples = ensureNonNullArray(evaluation.feedbackExamples);
    if (feedbackExamples.length <= 1) return;

    // Sort by grade and salience to get the "best" examples first
    evaluation.feedbackExamples = sortFeedbackExamplesByGradeAndSalience(
      feedbackExamples,
      evaluation.grade,
    );
  });

  // Second pass: For each evaluation, try to find a unique first quote
  evaluations.forEach(({ evaluation }) => {
    const feedbackExamples = ensureNonNullArray(evaluation.feedbackExamples);
    if (feedbackExamples.length <= 1) return;

    // Current first quote after grade/salience sorting
    const currentFirstQuote = feedbackExamples[0]?.quote;

    // If the current first quote isn't similar to any seen before, keep it and mark as seen
    if (currentFirstQuote && !isSimilarToAnySeenQuote(currentFirstQuote)) {
      seenFirstQuotes.push(currentFirstQuote);
      return;
    }

    // If first quote has been seen before, look for any quote that isn't similar to ones we've seen
    const topN = Math.min(feedbackExamples.length, 5); // Look through up to 5 examples
    let foundUniqueQuote = false;

    for (let i = 1; i < topN; i++) {
      const candidateQuote = feedbackExamples[i]?.quote;

      if (candidateQuote && !isSimilarToAnySeenQuote(candidateQuote)) {
        // Swap this unique quote to the first position
        const temp = feedbackExamples[i];
        feedbackExamples[i] = feedbackExamples[0];
        feedbackExamples[0] = temp;

        seenFirstQuotes.push(candidateQuote);
        foundUniqueQuote = true;
        break;
      }
    }

    // If we couldn't find a unique quote, just keep the best one by grade/salience
    // and record it as seen even though it's a duplicate
    if (!foundUniqueQuote && currentFirstQuote) {
      seenFirstQuotes.push(currentFirstQuote);
    }
  });

  return evaluations;
}

/**
 * Prepares evaluation criteria for display from a roleplay session.
 * Handles grouping, sorting, and optimizing first quotes for uniqueness.
 * Excludes low salience quotes entirely.
 *
 * @param evaluationCriteria - The evaluationCriteria data from the GraphQL query
 * @returns Processed criteria groups ready for rendering with unique first quotes
 */
export function prepareEvaluationCriteria(
  evaluations: Evaluation[],
): ProcessedCriterionGroup[] {
  if (!evaluations) return [];

  // Step 1: Group evaluations and collect those with feedback examples in one pass
  const groupedCriteriaMap = new Map<string, ProcessedCriterionGroup>();
  const evaluationsWithFeedback: EvaluationWithContext[] = [];

  // Create a deep clone of the evaluations for processing
  // Using structured clone would be faster but might not be available in all environments
  const evaluationsCopy = JSON.parse(JSON.stringify(evaluations));

  evaluationsCopy.forEach((evaluation: Evaluation) => {
    const criterion = evaluation.evaluationCriterionItem?.evaluationCriterion;
    if (!criterion) return;

    const { id, name, order = 0 } = criterion;

    // Add to or create group
    if (!groupedCriteriaMap.has(id)) {
      groupedCriteriaMap.set(id, {
        id,
        name,
        order,
        evaluations: [],
      });
    }

    const group = groupedCriteriaMap.get(id)!;
    group.evaluations.push(evaluation);

    // If this evaluation has feedback examples (after filtering), collect it for optimization
    if (evaluation.feedbackExamples && evaluation.feedbackExamples.length > 0) {
      evaluationsWithFeedback.push({
        evaluation,
        groupName: name,
      });
    }
  });

  // Step 2: Sort groups by order and evaluations within each group
  const result = Array.from(groupedCriteriaMap.values())
    .sort((a, b) => a.order - b.order)
    .map((group) => ({
      ...group,
      evaluations: group.evaluations.sort((a, b) => {
        // Sort by the order of the evaluationCriterionItem
        const orderA =
          a.evaluationCriterionItem?.evaluationCriterion?.order || 0;
        const orderB =
          b.evaluationCriterionItem?.evaluationCriterion?.order || 0;
        return orderA - orderB;
      }),
    }));

  // Step 3: Optimize first quotes if we have feedback examples
  if (evaluationsWithFeedback.length > 0) {
    optimizeFirstQuotes(evaluationsWithFeedback);
  }

  return result;
}
